import { motion } from 'motion/react';
import { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Megaphone, 
  Users, 
  Mail, 
  Share2, 
  TrendingUp, 
  BarChart3, 
  Sparkles,
  FileText,
  Zap,
  MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { marketingAPI, MarketingAnalytics } from '../lib/marketing-api';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';
import { LeadManagement } from './marketing/LeadManagement';
import { EmailCampaigns } from './marketing/EmailCampaigns';
import { SocialCampaigns } from './marketing/SocialCampaigns';
import { AIAgent } from './marketing/AIAgent';
import { EmailTemplates } from './marketing/EmailTemplates';
import { WhatsAppTemplates } from './marketing/WhatsAppTemplates';
import { AutomatedEmails } from './marketing/AutomatedEmails';

interface MarketingHubProps {
  onBackToCatalog?: () => void;
}

export function MarketingHub({ onBackToCatalog }: MarketingHubProps) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [templateTab, setTemplateTab] = useState('email');
  const [analytics, setAnalytics] = useState<MarketingAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      const { analytics: data } = await marketingAPI.getAnalytics();
      setAnalytics(data);
    } catch (error) {
      logger.error('Failed to load analytics', error as Error, undefined, 'MarketingHub');
      toast.error('Failed to load marketing analytics');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            {onBackToCatalog && (
              <Button
                variant="ghost"
                onClick={onBackToCatalog}
                className="gap-2"
                data-testid="marketing-back-to-catalog"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Catalog
              </Button>
            )}
          </div>

          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Megaphone className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-yellow-500 to-primary bg-clip-text text-transparent">
                Property Marketing Hub
              </h1>
              <p className="text-muted-foreground mt-1">
                Comprehensive CRM, campaign management, and analytics for property marketing
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <Card className="border-border/50 bg-card/50 backdrop-blur-sm" data-testid="analytics-marketing-total-leads-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground" data-testid="analytics-marketing-total-leads-label">Total Leads</p>
                      <p className="text-2xl font-bold" data-testid="analytics-marketing-total-leads-count">{analytics.leads.total}</p>
                      <Badge variant="secondary" className="mt-1" data-testid="analytics-marketing-new-leads-count">
                        {analytics.leads.new} new
                      </Badge>
                    </div>
                    <Users className="w-8 h-8 text-primary/50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 bg-card/50 backdrop-blur-sm" data-testid="analytics-marketing-email-campaigns-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground" data-testid="analytics-marketing-email-campaigns-label">Email Campaigns</p>
                      <p className="text-2xl font-bold" data-testid="analytics-marketing-email-campaigns-count">{analytics.emailCampaigns.total}</p>
                      <Badge variant="secondary" className="mt-1" data-testid="analytics-marketing-email-open-rate">
                        {analytics.emailCampaigns.avgOpenRate}% open rate
                      </Badge>
                    </div>
                    <Mail className="w-8 h-8 text-primary/50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 bg-card/50 backdrop-blur-sm" data-testid="analytics-marketing-social-campaigns-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground" data-testid="analytics-marketing-social-campaigns-label">Social Campaigns</p>
                      <p className="text-2xl font-bold" data-testid="analytics-marketing-social-campaigns-count">{analytics.socialCampaigns.total}</p>
                      <Badge variant="secondary" className="mt-1" data-testid="analytics-marketing-social-reach">
                        {analytics.socialCampaigns.totalReach.toLocaleString()} reach
                      </Badge>
                    </div>
                    <Share2 className="w-8 h-8 text-primary/50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 bg-card/50 backdrop-blur-sm" data-testid="analytics-marketing-conversion-rate-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground" data-testid="analytics-marketing-conversion-rate-label">Conversion Rate</p>
                      <p className="text-2xl font-bold" data-testid="analytics-marketing-conversion-rate-value">
                        {analytics.leads.total > 0
                          ? ((analytics.leads.converted / analytics.leads.total) * 100).toFixed(1)
                          : 0}%
                      </p>
                      <Badge variant="secondary" className="mt-1" data-testid="analytics-marketing-converted-count">
                        {analytics.leads.converted} converted
                      </Badge>
                    </div>
                    <TrendingUp className="w-8 h-8 text-primary/50" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </motion.div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 lg:w-auto">
            <TabsTrigger value="dashboard" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="leads" className="gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">CRM & Leads</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-2">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Campaigns</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Templates</span>
            </TabsTrigger>
            <TabsTrigger value="automation" className="gap-2">
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Automation</span>
            </TabsTrigger>
            <TabsTrigger value="social" className="gap-2">
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Social</span>
            </TabsTrigger>
            <TabsTrigger value="ai-agent" className="gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">AI Agent</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Marketing Analytics Dashboard
                </CardTitle>
                <CardDescription>
                  Comprehensive overview of your marketing performance
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="py-12 text-center text-muted-foreground">
                    Loading analytics...
                  </div>
                ) : analytics ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Lead Performance</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Leads</span>
                          <span className="font-bold">{analytics.leads.total}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">New Leads</span>
                          <span className="font-bold">{analytics.leads.new}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Qualified Leads</span>
                          <span className="font-bold">{analytics.leads.qualified}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Converted</span>
                          <span className="font-bold text-green-600">{analytics.leads.converted}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-muted-foreground">Avg. Lead Score</span>
                          <Badge variant="secondary">{analytics.leads.avgScore}/100</Badge>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Campaign Performance</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Email Campaigns</span>
                          <span className="font-bold">{analytics.emailCampaigns.total}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Emails Sent</span>
                          <span className="font-bold">{analytics.emailCampaigns.totalSent}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Avg. Open Rate</span>
                          <Badge className="bg-blue-500">{analytics.emailCampaigns.avgOpenRate}%</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Social Reach</span>
                          <span className="font-bold">{analytics.socialCampaigns.totalReach.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-muted-foreground">Social Engagement</span>
                          <Badge variant="secondary">{analytics.socialCampaigns.totalEngagement.toLocaleString()}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="py-12 text-center text-destructive">
                    Failed to load analytics
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Other tabs will be implemented in sub-components */}
          <TabsContent value="leads">
            <LeadManagement />
          </TabsContent>

          <TabsContent value="email">
            <EmailCampaigns />
          </TabsContent>

          <TabsContent value="templates">
            <Tabs value={templateTab} onValueChange={setTemplateTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 lg:w-auto">
                <TabsTrigger value="email" className="gap-2">
                  <Mail className="w-4 h-4" />
                  <span className="hidden sm:inline">Email Templates</span>
                </TabsTrigger>
                <TabsTrigger value="whatsapp" className="gap-2">
                  <MessageSquare className="w-4 h-4" />
                  <span className="hidden sm:inline">WhatsApp Templates</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="email">
                <EmailTemplates />
              </TabsContent>

              <TabsContent value="whatsapp">
                <WhatsAppTemplates />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="automation">
            <AutomatedEmails />
          </TabsContent>

          <TabsContent value="social">
            <SocialCampaigns />
          </TabsContent>

          <TabsContent value="ai-agent">
            <AIAgent />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}